import React, { useEffect, useState, useContext } from 'react';
import api from '../../utils/api';
import { CaseDetailContext } from './CaseDetailContext';
import CaseOutcomeReportModal from './CaseOutcomeReportModal';

/**
 * Outcome-report shell — exact port of Scale's CaseCRMSubmissionReport.
 *
 * Behavior:
 *   - On mount, GET /cases/:id/outcome-report.
 *   - dataFound === null  → render nothing (loading).
 *   - !dataFound && mediation_date in past → "Submit Case Outcome Report"
 *     button (`bg-[#4087FC]`, white text).
 *   - dataFound → "Open Case in CRM" placeholder + the matching status banner
 *     (Settled = green-50 with teal "Settled" pill; Not Settled = #FDF0F0 with
 *     #7C0002 pill; Future Mediation = #FDFBF0 with #7C4C00 "Future Settlement"
 *     pill).
 *
 * Local-dev bypass: when NODE_ENV !== 'production' the button shows even
 * without a past mediation date, mirroring Scale's REACT_APP_FRONTEND_API
 * gate so the feature is testable on localhost.
 */

const SettledIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
    <path
      d="M7.99967 1.83325C4.31794 1.83325 1.33301 4.81805 1.33301 8.49992C1.33301 12.1818 4.31794 15.1666 7.99967 15.1666C11.6818 15.1666 14.6663 12.1818 14.6663 8.49992C14.6663 4.81805 11.6817 1.83325 7.99967 1.83325ZM6.77674 11.9245L3.84127 8.98912L4.81967 8.01072L6.77674 9.96765L11.1797 5.56459L12.1581 6.54299L6.77674 11.9245Z"
      fill="#007C7A"
    />
  </svg>
);

const NotSettledIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path
      d="M9 1.5C4.85625 1.5 1.5 4.85625 1.5 9C1.5 13.1437 4.85625 16.5 9 16.5C13.1437 16.5 16.5 13.1437 16.5 9C16.5 4.85625 13.1437 1.5 9 1.5ZM12.75 11.6887L11.6887 12.75L9 10.0612L6.31125 12.75L5.25 11.6887L7.93875 9L5.25 6.31125L6.31125 5.25L9 7.93875L11.6887 5.25L12.75 6.31125L10.0612 9L12.75 11.6887Z"
      fill="#7C0002"
    />
  </svg>
);

const FutureIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
    <path
      d="M8.50033 1.41675C7.09938 1.41675 5.72989 1.83218 4.56504 2.6105C3.40019 3.38883 2.4923 4.4951 1.95618 5.78941C1.42006 7.08372 1.27979 8.50794 1.5531 9.88197C1.82641 11.256 2.50103 12.5181 3.49166 13.5088C4.48228 14.4994 5.74441 15.174 7.11844 15.4473C8.49247 15.7206 9.91669 15.5803 11.211 15.0442C12.5053 14.5081 13.6116 13.6002 14.3899 12.4354C15.1682 11.2705 15.5837 9.90103 15.5837 8.50008C15.5837 7.56988 15.4004 6.6488 15.0445 5.78941C14.6885 4.93002 14.1667 4.14916 13.509 3.49141C12.8513 2.83366 12.0704 2.31191 11.211 1.95593C10.3516 1.59996 9.43053 1.41675 8.50033 1.41675ZM9.20866 8.50008C9.2092 8.5933 9.19133 8.68571 9.15608 8.77201C9.12083 8.85832 9.0689 8.93681 9.00325 9.003L6.87825 11.128C6.74274 11.244 6.56844 11.3047 6.39017 11.2978C6.2119 11.2909 6.04279 11.217 5.91664 11.0909C5.79049 10.9647 5.71659 10.7956 5.7097 10.6173C5.70281 10.4391 5.76345 10.2648 5.8795 10.1292L7.792 8.20966V4.95841C7.792 4.77055 7.86662 4.59039 7.99946 4.45755C8.1323 4.32471 8.31247 4.25008 8.50033 4.25008C8.68819 4.25008 8.86836 4.32471 9.0012 4.45755C9.13403 4.59039 9.20866 4.77055 9.20866 4.95841V8.50008Z"
      fill="#7C4C00"
    />
  </svg>
);

const OutcomeCheckmark = () => (
  <svg className="size-5 text-green-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
      clipRule="evenodd"
    />
  </svg>
);

function SummaryBanner({ report }) {
  const isSettled = report.status === 'Settled';
  const isNotSettled = report.status === 'Not Settled';
  const isFuture = report.status === 'Future Mediation';

  // Settled: green-50 bg, teal pill labelled "Settled"
  if (isSettled) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="rounded-t-md border-b bg-green-50 p-4">
          <div className="flex">
            <div className="shrink-0"><OutcomeCheckmark /></div>
            <div className="ml-3">
              <p className="text-sm font-medium">Outcome</p>
              <p className="text-sm font-medium text-[#111927] mt-2">{report.terms || '—'}</p>
            </div>
            <div className="ml-auto pl-3">
              <div className="-mx-1.5 -my-1.5">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-md bg-green-50 p-1.5"
                >
                  <SettledIcon />
                  <span className="text-[#007C7A] font-bold">Settled</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Not Settled: pinkish bg #FDF0F0, dark-red pill
  if (isNotSettled) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="rounded-t-md border-b p-4" style={{ backgroundColor: '#FDF0F0' }}>
          <div className="flex">
            <div className="shrink-0"><OutcomeCheckmark /></div>
            <div className="ml-3">
              <p className="text-sm font-medium">Outcome</p>
              <p className="text-sm font-medium text-gray-900 mt-2">{report.reason || '—'}</p>
            </div>
            <div className="ml-auto pl-3">
              <div className="-mx-1.5 -my-1.5">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-md p-1.5 text-[#7C0002]"
                >
                  <NotSettledIcon />
                  <span className="text-[#7C0002] font-bold">Not Settled</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Future Mediation: amber bg #FDFBF0, brown pill labelled "Future Settlement"
  if (isFuture) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="rounded-t-md border-b p-4" style={{ backgroundColor: '#FDFBF0' }}>
          <div className="flex">
            <div className="shrink-0"><OutcomeCheckmark /></div>
            <div className="ml-3">
              <p className="text-sm font-medium">Outcome</p>
              <div className="max-w-4xl">
                <p className="text-sm font-medium text-gray-900 mt-2">{report.reason || '—'}</p>
              </div>
            </div>
            <div className="ml-auto pl-3">
              <div className="-mx-1.5 -my-1.5">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-md p-1.5 text-[#7C4C00]"
                >
                  <FutureIcon />
                  <span className="text-[#7C4C00] font-bold">Future Settlement</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default function CaseOutcomeReportSection({ onToast }) {
  const { caseData } = useContext(CaseDetailContext);
  const [report, setReport] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);

  const caseId = caseData?.id;

  useEffect(() => {
    let cancelled = false;
    if (!caseId) return undefined;
    setLoaded(false);
    api.get(`/cases/${caseId}/outcome-report`)
      .then((res) => {
        if (cancelled) return;
        setReport(res?.data?.data || null);
      })
      .catch(() => {
        if (cancelled) return;
        setReport(null);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => { cancelled = true; };
  }, [caseId]);

  if (!caseData) return null;
  if (!loaded) return null;

  // dataFound branch — show "Open Case in CRM" link + summary banner.
  if (report) {
    return (
      <>
        <div className="py-4 pb-4 w-fit">
          <button
            type="button"
            onClick={() => onToast?.({ type: 'warning', message: 'CRM is handled in Scale Portal' })}
            className="px-14 py-2.5 text-sm font-bold text-white bg-[#4087FC] rounded-lg flex items-center gap-1.5"
          >
            Open Case in CRM
          </button>
        </div>
        <div className="mb-3">
          <SummaryBanner report={report} />
        </div>
      </>
    );
  }

  // Visibility gate — past mediation date, with a localhost dev bypass.
  const mediationDate = caseData.date || caseData.mediation_date;
  const past = mediationDate && new Date(mediationDate) < new Date();
  const devBypass = process.env.NODE_ENV !== 'production';
  if (!past && !devBypass) return null;

  return (
    <>
      <div className="py-4">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="px-5 py-2.5 text-sm font-bold text-white bg-[#4087FC] rounded-lg flex items-center gap-1.5"
        >
          Submit Case Outcome Report
        </button>
      </div>
      <CaseOutcomeReportModal
        isOpen={open}
        onClose={() => setOpen(false)}
        caseData={caseData}
        onToast={onToast}
        onSubmitted={(saved) => {
          setReport(saved);
          setOpen(false);
        }}
      />
    </>
  );
}
